"use client";
import { setVehicleSearchOwner } from "@/lib/vehicle/corporateSearch";

import {
  FlightCredit,
  FlightCreditsResponse,
  Itineraries,
  LoyaltyAccount,
  LoyaltyAccountsResponse,
  LoyaltyAccountsResponseFromJSON,
  CardProgramSummary,
  LoyaltyProgram,
  FreeNightCredit,
  FreeNightCreditsResponse,
  PooledLoyaltyAccount,
  PooledLoyaltyAccountsResponse,
  SharedFromUser,
  UpgradeCertificate,
  UpgradeCertificatesResponse,
  UserStatus,
  UsersUserIdWelcomeActionsGetRequest,
  WelcomeActions,
  User,
  CurrencyAmount,
  UserCreditsSlim,
} from "@/generated/email_parser.openapi";

export interface AccountWithOwnership extends LoyaltyAccount {
  sharedFrom?: SharedFromUser;
}
import { formatCurrency } from "@/lib/utils";
import { detectCurrencyFromLocale } from "@/lib/currency";
import { safeResponseJson } from "@/lib/api/errorHandling";
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  USE_ONBOARDING_MOCK_DATA,
  getMockItineraries,
  getMockLoyaltyAccountsResponse,
} from "@/lib/mock/onboardingMockData";
import { filterLoyaltyAccounts } from "@/app/(app)/loyalty-dashboard/helpers";
import {
  filterActiveCertificates,
  filterActiveCredits,
  filterActiveFreeNightCredits,
} from "@/app/(app)/loyalty-dashboard/creditUtils";
import { GondolaLoading } from "@/components/ui/gondolaLoading";
import {
  GmailReSyncModal,
  RatePastBookingModal,
} from "@/components/modal/welcomeActionsModal";
import {
  SearchSessionCollection,
  SearchSessionV2,
  RecentHotel,
  RecentHotelCollectionFromJSON,
} from "@/generated/search.openapi";
import { Session } from "next-auth";
import { signOut } from "next-auth/react";
import { usePostHog } from "posthog-js/react";
import { usePathname } from "next/navigation";
import { isAuthPage } from "@/lib/apiUtils";

export type EmailData = {
  email: string;
  cashValue: number;
};

type GlobalStoreContextType = {
  user?: {
    data: User;
    status: UserStatus;
    fetchUser?: () => Promise<User | undefined>;
    isTemporaryFullAccess?: boolean;
    setIsTemporaryFullAccess: Dispatch<SetStateAction<boolean>>;
  };
  loyaltyData: {
    totalLoyaltyAccounts: number;
    loyaltyAccounts: LoyaltyAccount[];
    cardProgramSummaries?: CardProgramSummary[];
    allLoyaltyAccountsIncludingDeleted: LoyaltyAccount[];
    activeLoyaltyAccounts: LoyaltyAccount[];
    loyaltyPrograms: LoyaltyProgram[];
    refetchLoyaltyAccounts: (
      includeDeleted?: boolean
    ) => Promise<LoyaltyAccountsResponse | undefined>;
    refetchAllLoyaltyAccountsIncludingDeleted: () => Promise<
      LoyaltyAccountsResponse | undefined
    >;
    refetchAllLoyaltyData: () => Promise<void>;
    totalCash?: string;
    totalPointsUpdate?: {
      string: string;
      value: number;
    };
    allEmailData: EmailData[];
    hasMultipleEmails: boolean;
    loyaltyAccountsLoading: boolean;
    pooledAccounts: PooledLoyaltyAccount[];
    allAccountsWithOwnership: AccountWithOwnership[];
    flightCredits: FlightCredit[];
    upgradeCertificates: UpgradeCertificate[];
    freeNightCredits: FreeNightCredit[];
    refetchFlightCredits: () => Promise<void>;
    refetchUpgradeCertificates: () => Promise<void>;
    refetchFreeNightCredits: () => Promise<void>;
  };
  userCreditsData: {
    totalCashValue: number;
    totalCashValueString: string;
    userCredits?: UserCreditsSlim;
  };
  tripsData: {
    fetchItineraries: () => Promise<Itineraries | undefined>;
    itineraries: Itineraries | undefined;
  };

  refetchDataPostBooking: () => void;
  welcomeActions: {
    data?: WelcomeActions;
    refetchWelcomeActions: () => Promise<WelcomeActions | undefined>;
    checkAndOpenGmailReSyncModal: () => void;
    checkAndOpenRatingsModal: () => void;
  };
  searchData: {
    recentSearchSessions: SearchSessionV2[];
    recentHotels: RecentHotel[];
    hasAlreadyFetched: boolean;
    fetchRecentSearchSession: () => Promise<SearchSessionV2[] | undefined>;
    fetchRecentHotels: () => Promise<RecentHotel[] | undefined>;
  };
  missingCardModal: {
    showCheckMissingCardModal: boolean;
    setShowCheckMissingCardModal: Dispatch<SetStateAction<boolean>>;
  };
};

const defaultState: GlobalStoreContextType = {
  user: undefined,
  loyaltyData: {
    totalLoyaltyAccounts: 0,
    loyaltyAccounts: [],
    allLoyaltyAccountsIncludingDeleted: [],
    activeLoyaltyAccounts: [],
    loyaltyPrograms: [],
    refetchLoyaltyAccounts: async () => undefined,
    refetchAllLoyaltyAccountsIncludingDeleted: async () => undefined,
    refetchAllLoyaltyData: async () => {},
    totalCash: "",
    totalPointsUpdate: {
      string: "",
      value: 0,
    },
    allEmailData: [],
    hasMultipleEmails: false,
    loyaltyAccountsLoading: true,
    pooledAccounts: [],
    allAccountsWithOwnership: [],
    flightCredits: [],
    upgradeCertificates: [],
    freeNightCredits: [],
    refetchFlightCredits: async () => {},
    refetchUpgradeCertificates: async () => {},
    refetchFreeNightCredits: async () => {},
  },
  userCreditsData: {
    totalCashValue: 0,
    totalCashValueString: "",
    userCredits: undefined,
  },
  tripsData: {
    fetchItineraries: async () => undefined,
    itineraries: undefined,
  },
  refetchDataPostBooking: () => {},
  welcomeActions: {
    data: undefined,
    refetchWelcomeActions: async () => undefined,
    checkAndOpenGmailReSyncModal: () => {},
    checkAndOpenRatingsModal: () => {},
  },
  searchData: {
    recentSearchSessions: [],
    recentHotels: [],
    hasAlreadyFetched: false,
    fetchRecentSearchSession: async () => undefined,
    fetchRecentHotels: async () => undefined,
  },
  missingCardModal: {
    showCheckMissingCardModal: false,
    setShowCheckMissingCardModal: () => {},
  },
};

const GlobalStoreContext = createContext<GlobalStoreContextType>(defaultState);

type GlobalStoreProviderProps = PropsWithChildren<{
  session: Session | null;
  /** Authorized support target, resolved by the server layout. */
  effectiveUserId?: string;
}>;

export default function GlobalStoreProvider(props: GlobalStoreProviderProps) {
  // A new signed-in identity or support target gets a fresh snapshot immediately.
  // Responses still in flight
  // for the previous identity can only update its unmounted provider.
  return (
    <GlobalStoreSessionProvider
      key={JSON.stringify([
        props.session?.user?.id ?? null,
        props.effectiveUserId ?? props.session?.user?.id ?? null,
      ])}
      {...props}
    />
  );
}

function GlobalStoreSessionProvider({
  children,
  session,
}: GlobalStoreProviderProps) {
  const pathname = usePathname();

  // Skip API calls on auth pages - they redirect immediately and interrupt requests
  const shouldSkipApiCalls = isAuthPage(pathname);
  const [cardProgramSummaries, setCardProgramSummaries] =
    useState<CardProgramSummary[]>();
  const [loyaltyAccounts, setLoyaltyAccounts] = useState<LoyaltyAccount[]>([]);
  const [
    allLoyaltyAccountsIncludingDeleted,
    setAllLoyaltyAccountsIncludingDeleted,
  ] = useState<LoyaltyAccount[]>([]);
  const [loyaltyPrograms, setLoyaltyPrograms] = useState<LoyaltyProgram[]>([]);
  const [totalLoyaltyAccounts, setTotalLoyaltyAccounts] = useState(0);
  const [activeLoyaltyAccounts, setActiveLoyaltyAccounts] = useState<
    LoyaltyAccount[]
  >([]);
  const [loyaltyAccountsLoading, setLoyaltyAccountsLoading] = useState(
    defaultState.loyaltyData.loyaltyAccountsLoading
  );
  const [userCredits, setUserCredits] = useState<UserCreditsSlim>();
  const [flightCredits, setFlightCredits] = useState<FlightCredit[]>([]);
  const [upgradeCertificates, setUpgradeCertificates] = useState<
    UpgradeCertificate[]
  >([]);
  const [freeNightCredits, setFreeNightCredits] = useState<FreeNightCredit[]>(
    []
  );
  const [pooledAccounts, setPooledAccounts] = useState<PooledLoyaltyAccount[]>(
    []
  );
  const [initialLoading, setInitialLoading] = useState(true);

  const [itineraries, setItineraries] = useState<Itineraries | undefined>();
  const [welcomeActions, setWelcomeActions] = useState<WelcomeActions>();
  const [gmailReSyncModalOpen, setGmailReSyncModalOpen] = useState(false);
  const [ratePastStayActions, setRatePastStayActions] = useState(false);
  const [recentSearchSessions, setRecentSearchSessions] = useState<
    SearchSessionV2[]
  >([]);
  const [recentHotels, setRecentHotels] = useState<RecentHotel[]>([]);
  const [userLoading, setUserLoading] = useState(true);
  const [initUserCall, setInitUserCall] = useState(false);
  const [userData, setUserData] = useState<User>();
  const [hasAlreadyFetched, setHasAlreadyFetched] = useState(false);
  const [totalCashValue, setTotalCashValue] = useState<CurrencyAmount>();
  const [pointsUpdateTotal, setPointsUpdateTotal] = useState<CurrencyAmount>();
  const [isTemporaryFullAccess, setIsTemporaryFullAccess] = useState(false);
  const [showCheckMissingCardModal, setShowCheckMissingCardModal] =
    useState(false);
  const posthog = usePostHog();

  const fetchUser = useCallback(async () => {
    try {
      const userData = await fetch("/api/user/self").then((res) =>
        safeResponseJson<User>(res)
      );
      setUserData(userData);
      return userData;
    } catch (error) {
      posthog?.reset();
      setVehicleSearchOwner(null);
      signOut({ callbackUrl: "/" });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserCredits = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch("/api/user/credits", {
        signal,
      });
      if (!response.ok) {
        return;
      }
      const userCredits = await safeResponseJson<UserCreditsSlim>(response);
      setUserCredits(userCredits);
      return userCredits;
    } catch (error) {
      // Silently handle abort errors (navigation interruptions)
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      return;
    }
  }, []);

  const fetchFlightCredits = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch("/api/user/flight_credits/get", { signal });
      if (!response.ok) return;
      const data = (await response.json()) as FlightCreditsResponse;
      setFlightCredits(filterActiveCredits(data.credits ?? []));
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
    }
  }, []);

  const fetchUpgradeCertificates = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch("/api/user/upgrade_certificates/get", {
        signal,
      });
      if (!response.ok) return;
      const data = (await response.json()) as UpgradeCertificatesResponse;
      setUpgradeCertificates(filterActiveCertificates(data.certificates ?? []));
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
    }
  }, []);

  const fetchFreeNightCredits = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch("/api/user/free_night_credits/get", {
        signal,
      });
      if (!response.ok) return;
      const data = (await response.json()) as FreeNightCreditsResponse;
      setFreeNightCredits(filterActiveFreeNightCredits(data.credits ?? []));
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
    }
  }, []);

  const fetchPooledAccounts = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch("/api/user/family_pool/pooled-accounts", {
        signal,
      });
      if (!response.ok) return;
      const data =
        await safeResponseJson<PooledLoyaltyAccountsResponse>(response);
      setPooledAccounts(data.pooledAccounts ?? []);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
    }
  }, []);

  const refetchFlightCredits = useCallback(async () => {
    await fetchFlightCredits();
  }, [fetchFlightCredits]);

  const refetchUpgradeCertificates = useCallback(async () => {
    await fetchUpgradeCertificates();
  }, [fetchUpgradeCertificates]);

  const refetchFreeNightCredits = useCallback(async () => {
    await fetchFreeNightCredits();
  }, [fetchFreeNightCredits]);

  const refetchLoyaltyAccounts = useCallback(
    async (includeDeleted: boolean = false, signal?: AbortSignal) => {
      try {
        if (USE_ONBOARDING_MOCK_DATA) {
          const result = getMockLoyaltyAccountsResponse();
          if (includeDeleted) {
            setAllLoyaltyAccountsIncludingDeleted([]);
          } else {
            setPointsUpdateTotal(result.pointsUpdateTotal);
            setTotalCashValue(result.totalCashValue);
            const activeAccounts = result.items?.filter(filterLoyaltyAccounts);
            setActiveLoyaltyAccounts(activeAccounts || []);
            setTotalLoyaltyAccounts(
              Math.max(result.accountTotal ?? 0, result.items?.length ?? 0)
            );
            setLoyaltyAccounts(result.items || []);
            setCardProgramSummaries(result.cardProgramSummaries);
          }
          return result;
        }

        // Explicitly set extended parameter to ensure correct filtering
        const url = includeDeleted
          ? `/api/user/loyalty_accounts/v2/get?extended=false`
          : `/api/user/loyalty_accounts/v2/get?extended=true`;

        const response = await fetch(url, { signal });
        if (!response.ok) {
          return;
        }
        const result = LoyaltyAccountsResponseFromJSON(
          await safeResponseJson<unknown>(response)
        );

        if (includeDeleted) {
          // When fetching all accounts including deleted, only update the deleted accounts list
          setAllLoyaltyAccountsIncludingDeleted(result.items || []);
        } else {
          // When fetching regular accounts, update all the main state
          setPointsUpdateTotal(result.pointsUpdateTotal);
          setTotalCashValue(result.totalCashValue);
          const activeAccounts = result.items?.filter(filterLoyaltyAccounts);
          setActiveLoyaltyAccounts(activeAccounts || []);
          // Use Math.max to handle edge cases where accountTotal might be 0 but there are items
          setTotalLoyaltyAccounts(
            Math.max(result.accountTotal ?? 0, result.items?.length ?? 0)
          );
          setLoyaltyAccounts(result.items || []);
          setCardProgramSummaries(result.cardProgramSummaries);
        }

        return result;
      } catch (error) {
        // Silently handle abort errors (navigation interruptions)
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        return;
      }
    },
    []
  );

  const refetchAllLoyaltyAccountsIncludingDeleted = useCallback(async () => {
    return refetchLoyaltyAccounts(true);
  }, [refetchLoyaltyAccounts]);

  const refetchAllLoyaltyData = useCallback(async () => {
    // Refresh regular accounts, deleted accounts, and pooled-from-others
    // accounts together so the dashboard's own / deleted / FamilyPool sections
    // stay in sync (e.g. after accepting a pool invitation).
    await Promise.all([
      refetchLoyaltyAccounts(false),
      refetchLoyaltyAccounts(true),
      fetchPooledAccounts(),
    ]);
  }, [refetchLoyaltyAccounts, fetchPooledAccounts]);

  const fetchLoyaltyPrograms = useCallback(
    async (signal?: AbortSignal) => {
      try {
        // Use public endpoint for non-authenticated users
        const endpoint = session?.user
          ? `/api/loyalty_programs`
          : `/api/public/loyalty_programs`;
        const response = await fetch(endpoint, { signal });
        if (!response.ok) {
          return;
        }
        const result = await safeResponseJson<LoyaltyProgram[]>(response);
        setLoyaltyPrograms(result || []);
        return result;
      } catch (error) {
        // Silently handle abort errors (navigation interruptions)
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        return;
      }
    },
    [session?.user]
  );

  const fetchItineraries = useCallback(async (signal?: AbortSignal) => {
    try {
      if (USE_ONBOARDING_MOCK_DATA) {
        const data = getMockItineraries();
        setItineraries(data);
        return data;
      }

      const response = await fetch(`/api/user/trips/v2`, { signal });
      if (!response.ok) {
        return;
      }
      const data = await safeResponseJson<Itineraries>(response);
      setItineraries(data);
      return data;
    } catch (error) {
      // Silently handle abort errors (navigation interruptions)
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      return;
    }
  }, []);

  const fetchRecentSearchSession = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch(`/api/search/hotel/recent`, { signal });
      if (!response.ok) {
        setHasAlreadyFetched(true);
        return;
      }
      const data = await safeResponseJson<SearchSessionCollection>(response);
      const items = data.items || [];
      setRecentSearchSessions(items);
      return items;
    } catch (error) {
      // Silently handle abort errors (navigation interruptions)
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      return;
    } finally {
      setHasAlreadyFetched(true);
    }
  }, []);

  const fetchRecentHotels = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch(
        `/api/search/hotel/recently-viewed?limit=10`,
        {
          signal,
        }
      );
      if (!response.ok) return;
      const data = RecentHotelCollectionFromJSON(
        await safeResponseJson<unknown>(response)
      );
      const items = data.items || [];
      setRecentHotels(items);
      return items;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      return;
    }
  }, []);

  const refetchDataPostBooking = () => {
    fetchUserCredits();
    fetchItineraries();
  };

  const fetchWelcomeActions = useCallback(
    async (signal?: AbortSignal) => {
      try {
        if (!session?.user.id) return;
        const request: UsersUserIdWelcomeActionsGetRequest = {
          userId: session?.user.id,
        };
        const response = await fetch("/api/user/welcome_actions", {
          method: "POST",
          body: JSON.stringify(request),
          signal,
        });
        if (!response.ok) {
          return;
        }
        const data = await safeResponseJson<WelcomeActions>(response);
        setWelcomeActions(data);
        return data;
      } catch (error) {
        // Silently handle abort errors (navigation interruptions)
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        return;
      }
    },
    [session?.user.id]
  );

  const checkAndOpenGmailReSyncModal = useCallback(() => {
    const hasGmailReSyncAction = Boolean(
      welcomeActions?.emailSyncTokenExpiredWelcomeAction?.id
    );
    if (hasGmailReSyncAction) {
      setGmailReSyncModalOpen(true);
    }
  }, [welcomeActions]);

  const checkAndOpenRatingsModal = useCallback(() => {
    const hasPastRatingAction = Boolean(
      (welcomeActions?.ratePastStayWelcomeActions.items.length ?? 0) > 0
    );
    setRatePastStayActions(hasPastRatingAction);
  }, [welcomeActions]);

  const canFetchData = useMemo(() => {
    if (userLoading && Boolean(session?.user)) return "loading";
    return (
      Boolean(session?.user) &&
      (userData?.status === "EMAIL_SYNCED" ||
        userData?.status === "SITE_ACCESS_FULL")
    );
  }, [session?.user, userData?.status, userLoading]);

  useEffect(() => {
    // Skip on auth pages to prevent interrupted requests
    if (shouldSkipApiCalls) return;
    if (canFetchData === "loading") return;
    if (canFetchData) {
      const abortController = new AbortController();
      fetchWelcomeActions(abortController.signal);

      // Cleanup: abort request on unmount or dependency change
      return () => {
        abortController.abort();
      };
    }
  }, [fetchWelcomeActions, canFetchData, shouldSkipApiCalls]);

  useEffect(() => {
    if (Boolean(session?.user) && !initUserCall) {
      setInitUserCall(true);
      (async () => {
        setUserLoading(true);
        const user = await fetchUser();
        // Auto-detect and persist currency on first login if not already set.
        // Prefer server-side IP geolocation (/api/geo/currency) over the
        // browser locale heuristic — `navigator.language` reflects language
        // preference, not location, which mis-routes Ukrainian users on
        // Russian-language browsers to RUB instead of UAH (GON-5110).
        if (user && !user.preferredCurrency) {
          try {
            let detected: string | null = null;
            try {
              const geoRes = await fetch("/api/geo/currency");
              if (geoRes.ok) {
                const geo = await safeResponseJson<{
                  currency: string | null;
                }>(geoRes);
                if (geo.currency) {
                  detected = geo.currency;
                }
              }
            } catch {
              // Geo lookup failed — fall through to locale heuristic.
            }
            if (!detected) {
              detected = detectCurrencyFromLocale();
            }
            await fetch("/api/user/preferredCurrency", {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ preferredCurrency: detected }),
            });
            setUserData((prev) =>
              prev ? { ...prev, preferredCurrency: detected } : prev
            );
          } catch {
            // Non-critical — silently continue with USD default
          }
        }
        setUserLoading(false);
      })();
    }
  }, [fetchUser, initUserCall, session]);

  useEffect(() => {
    // Skip on auth pages to prevent interrupted requests
    if (shouldSkipApiCalls) {
      setLoyaltyAccountsLoading(false);
      setInitialLoading(false);
      return;
    }

    if (canFetchData === "loading") return;
    setLoyaltyAccountsLoading(true);

    const abortController = new AbortController();

    (async () => {
      try {
        if (canFetchData) {
          await refetchLoyaltyAccounts(false, abortController.signal);
        }
        await fetchLoyaltyPrograms(abortController.signal);
        setLoyaltyAccountsLoading(false);
        if (canFetchData) {
          await fetchUserCredits(abortController.signal);
        }
      } catch (error) {
        // Silently handle abort errors (navigation interruptions)
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        // eslint-disable-next-line no-console
        console.error("Error fetching loyalty data:", error);
      } finally {
        setInitialLoading(false);
      }
    })();

    // Cleanup: abort any pending requests on unmount or dependency change
    return () => {
      abortController.abort();
    };
  }, [
    refetchLoyaltyAccounts,
    fetchUserCredits,
    fetchLoyaltyPrograms,
    canFetchData,
    session?.user,
    userLoading,
    shouldSkipApiCalls,
  ]);

  useEffect(() => {
    if (!userLoading) {
      setInitialLoading(false);
    }
  }, [userLoading]);

  // One-shot fetch of flight credits + upgrade certificates on mount.
  // Deliberately not part of the polling loop (mirrors mobile's model) — these
  // change rarely and only via explicit user action (add/edit/delete sheets),
  // which call refetchFlightCredits / refetchUpgradeCertificates directly.
  useEffect(() => {
    if (shouldSkipApiCalls) return;
    if (canFetchData === "loading") return;
    if (!canFetchData) return;
    const abortController = new AbortController();
    fetchFlightCredits(abortController.signal);
    fetchUpgradeCertificates(abortController.signal);
    fetchFreeNightCredits(abortController.signal);
    fetchPooledAccounts(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [
    canFetchData,
    shouldSkipApiCalls,
    fetchFlightCredits,
    fetchUpgradeCertificates,
    fetchFreeNightCredits,
    fetchPooledAccounts,
  ]);

  const allAccountsWithOwnership: AccountWithOwnership[] = useMemo(() => {
    const own: AccountWithOwnership[] = activeLoyaltyAccounts;
    const pooled: AccountWithOwnership[] = pooledAccounts
      .map((p) => p.loyaltyAccount)
      .filter(filterLoyaltyAccounts)
      .map((la) => {
        const entry = pooledAccounts.find((p) => p.loyaltyAccount === la);
        return { ...la, sharedFrom: entry?.sharedFromUser };
      });
    return [...own, ...pooled];
  }, [activeLoyaltyAccounts, pooledAccounts]);

  const totalCashValWithCurrency = useMemo(
    () =>
      formatCurrency(
        totalCashValue?.value ?? 0,
        totalCashValue?.currency ?? "USD",
        0
      ),
    [totalCashValue]
  );

  const allEmailData: EmailData[] = useMemo(() => {
    if (!loyaltyAccounts || loyaltyAccounts.length === 0) {
      if (userData?.status === UserStatus.SiteAccessFull) {
        return [{ email: userData?.email as string, cashValue: 0 }];
      }
      return [];
    }
    const emailArr = loyaltyAccounts
      ?.map((account) => account.account?.email ?? userData?.email)
      .filter((value, index, self) => self.indexOf(value) === index)
      .filter(Boolean) as string[];

    return emailArr.map((email) => {
      return {
        email,
        cashValue: loyaltyAccounts.reduce((acc, curr) => {
          if (curr.account?.email === email) {
            return acc + (curr.cashValue || 0);
          }
          return acc;
        }, 0),
      };
    });
  }, [loyaltyAccounts, userData]);

  const loyaltyData = useMemo(
    () => ({
      loyaltyAccounts,
      cardProgramSummaries,
      allLoyaltyAccountsIncludingDeleted,
      totalLoyaltyAccounts,
      activeLoyaltyAccounts,
      loyaltyPrograms,
      refetchLoyaltyAccounts,
      refetchAllLoyaltyAccountsIncludingDeleted,
      refetchAllLoyaltyData,
      totalCash: totalCashValWithCurrency,
      totalPointsUpdate: pointsUpdateTotal
        ? {
            string: formatCurrency(
              pointsUpdateTotal.value,
              pointsUpdateTotal.currency,
              0
            ),
            value: pointsUpdateTotal.value,
          }
        : undefined,
      allEmailData,
      hasMultipleEmails: allEmailData.length > 1,
      loyaltyAccountsLoading,
      pooledAccounts,
      allAccountsWithOwnership,
      flightCredits,
      upgradeCertificates,
      freeNightCredits,
      refetchFlightCredits,
      refetchUpgradeCertificates,
      refetchFreeNightCredits,
    }),
    [
      loyaltyAccounts,
      cardProgramSummaries,
      allLoyaltyAccountsIncludingDeleted,
      totalLoyaltyAccounts,
      activeLoyaltyAccounts,
      loyaltyPrograms,
      refetchLoyaltyAccounts,
      refetchAllLoyaltyAccountsIncludingDeleted,
      refetchAllLoyaltyData,
      totalCashValWithCurrency,
      pointsUpdateTotal,
      allEmailData,
      loyaltyAccountsLoading,
      pooledAccounts,
      allAccountsWithOwnership,
      flightCredits,
      upgradeCertificates,
      freeNightCredits,
      refetchFlightCredits,
      refetchUpgradeCertificates,
      refetchFreeNightCredits,
    ]
  );

  return (
    <GlobalStoreContext.Provider
      value={{
        user: userData
          ? {
              data: userData,
              status: userData.status as UserStatus,
              fetchUser,
              isTemporaryFullAccess,
              setIsTemporaryFullAccess,
            }
          : undefined,
        loyaltyData: loyaltyData,
        userCreditsData: {
          totalCashValue: totalCashValue?.value ?? 0,
          totalCashValueString: totalCashValWithCurrency,
          userCredits,
        },

        tripsData: {
          fetchItineraries,
          itineraries,
        },
        refetchDataPostBooking,
        welcomeActions: {
          data: welcomeActions,
          refetchWelcomeActions: fetchWelcomeActions,
          checkAndOpenGmailReSyncModal,
          checkAndOpenRatingsModal,
        },
        searchData: {
          recentSearchSessions,
          recentHotels,
          hasAlreadyFetched,
          fetchRecentSearchSession,
          fetchRecentHotels,
        },
        missingCardModal: {
          showCheckMissingCardModal,
          setShowCheckMissingCardModal,
        },
      }}
    >
      {initialLoading ? (
        <div className="h-screen w-screen place-content-center bg-background">
          <GondolaLoading />
        </div>
      ) : (
        <>
          <GmailReSyncModal
            open={gmailReSyncModalOpen}
            onOpenChange={setGmailReSyncModalOpen}
          />
          {!gmailReSyncModalOpen && (
            <RatePastBookingModal
              open={ratePastStayActions}
              onOpenChange={setRatePastStayActions}
            />
          )}
          {children}
        </>
      )}
    </GlobalStoreContext.Provider>
  );
}

export const useGlobalStore = () => {
  const {
    user,
    loyaltyData,
    userCreditsData,
    tripsData,
    refetchDataPostBooking,
    welcomeActions,
    searchData,
    missingCardModal,
  } = useContext(GlobalStoreContext);

  return {
    user,
    loyaltyData,
    userCreditsData,
    tripsData,
    refetchDataPostBooking,
    welcomeActions,
    searchData,
    missingCardModal,
  };
};
